import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import PropTypes from 'prop-types';
import axios from 'axios';
import { connect } from 'react-redux';
import Button from '@mui/material/Button';
import { headers } from 'actions/defaults';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import DataLoadingSpinner from 'components/global/DataLoadingSpinner';
import AddSponsoredMinutesDialog from './AddSponsoredMinutesDialog';
import { API_URL } from '../../../environment';
import { fetchClient } from '../../../actions/admin/user';
import ServicesAvailable from './services_available/index';

const isAdmin = window.location.pathname.split('/')[1] === 'admin';

const sectionStyle = {
  marginTop: '32px',
};

const titleStyle = {
  textTransform: 'uppercase',
  fontSize: '12px',
  fontWeight: '600',
  marginBottom: '8px',
  color: '#3c75c7',
  letterSpacing: '0.2px',
};

const descStyle = {
  wordWrap: 'break-word',
  fontSize: '16px',
  marginBottom: '16px',
};

function AvailableServicesBody({
  client,
  fetchClient: fetchClientAction,
  flags,
}) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [message, setMessage] = useState(null);

  const handleOpenDialog = () => {
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const handleSubmit = async data => {
    try {
      const url = `${API_URL}/admin/additional_minutes/add`;

      const response = await axios.put(
        url,
        {
          client_id: client.id,
          counselling_type: data.counsellingType,
          hours: data.hours,
        },
        headers(),
      );
      setMessage(response.data.message);
    } catch (error) {
      setErrorMessage(error.response?.data?.error);
    } finally {
      handleCloseDialog();
      setSnackOpen(true);
      fetchClientAction(client.id);
    }
  };

  const renderIntro = () => {
    const { available_services: services } = client;

    const hasAnyServices = Object.values(services).some(service => service);

    return (
      <div style={sectionStyle}>
        <p style={descStyle}>
          {hasAnyServices ? (
            <FormattedMessage defaultMessage="This client has access to the following services through GreenShield Health." />
          ) : (
            <FormattedMessage defaultMessage="This client does not have access to any additional services through GreenShield Health at this time." />
          )}
        </p>
      </div>
    );
  };

  const renderPlanSponsor = () => (
    <div style={sectionStyle}>
      <div style={sectionStyle}>
        <p style={titleStyle}>
          <FormattedMessage defaultMessage="Corporate Plan Sponsor Information" />
        </p>
        <div>
          <p style={descStyle}>
            {client.company?.name || (
              <FormattedMessage defaultMessage="N/A" />
            )}
          </p>
        </div>
      </div>
    </div>
  );

  const renderReseller = () => (
    <div style={sectionStyle}>
      <div style={sectionStyle}>
        <p style={titleStyle}>
          <FormattedMessage defaultMessage="Reseller Company" />
        </p>
        <div>{client.reseller && <p>{client.reseller.name}</p>}</div>
      </div>
    </div>
  );

  const renderEapProductBrandName = () => {
    let brandName;

    // Handle missing (undefined) and error (null) cases differently:
    // - undefined: client is missing profile ID, show "N/A"
    // - null: profile ID present but error fetching, show error message
    switch (client.eap_product_brand_name) {
      case undefined:
        brandName = <FormattedMessage defaultMessage="N/A" />;
        break;
      case null:
        brandName = (
          <FormattedMessage defaultMessage="We’re unable to display the product brand name at this time." />
        );
        break;
      default:
        brandName = client.eap_product_brand_name;
    }

    return (
      <div style={sectionStyle}>
        <div style={sectionStyle}>
          <p style={titleStyle}>
            <FormattedMessage defaultMessage="Product Brand Name" />
          </p>
          <div>
            <p>{brandName}</p>
          </div>
        </div>
      </div>
    );
  };

  const getHour = hour => {
    if (hour === 'Unlimited') {
      return <FormattedMessage defaultMessage="Unlimited" />;
    }
    return hour;
  };

  const renderRemainingSponsoredHours = () => {
    const { sponsored_hours: sponsoredHours } = client;

    return (
      <div style={sectionStyle}>
        <div style={sectionStyle}>
          <p style={titleStyle}>
            <FormattedMessage defaultMessage="Remaining Sponsored Hours" />
          </p>
          <div>
            {'individual' in sponsoredHours && (
              <p style={descStyle}>
                <FormattedMessage
                  defaultMessage="<b>Individual</b>: {hours} hours"
                  values={{
                    hours: getHour(sponsoredHours.individual),
                    b: text => <b>{text}</b>,
                  }}
                />
              </p>
            )}
            {'couples' in sponsoredHours && (
              <p style={descStyle}>
                <FormattedMessage
                  defaultMessage="<b>Couples</b>: {hours} hours"
                  values={{
                    hours: getHour(sponsoredHours.couples),
                    b: text => <b>{text}</b>,
                  }}
                />
              </p>
            )}
            {'generic' in sponsoredHours &&
              sponsoredHours.generic.map(item => {
                const providerInfo =
                  item.provider_id &&
                  ` (${item.role_name}, ${item.provider_id})`;

                return (
                  <p style={descStyle}>
                    <FormattedMessage
                      defaultMessage="<b>{role}</b>: {hours} hours"
                      values={{
                        role: `${item.category_name}${providerInfo || ''}`,
                        hours: getHour(item.hours),
                        b: text => <b>{text}</b>,
                      }}
                    />
                  </p>
                );
              })}
          </div>
        </div>
        {isAdmin && (
          <>
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenDialog}
            >
              <FormattedMessage defaultMessage="Add Additional Hours" />
            </Button>
            <AddSponsoredMinutesDialog
              open={isDialogOpen}
              onClose={handleCloseDialog}
              onSubmit={handleSubmit}
            />
          </>
        )}
      </div>
    );
  };

  const renderRemainingInsuranceSponsoredHours = () => {
    const { insurance_coverage: coverage } = client;

    if (!coverage?.coverage_yn) {
      return <></>;
    }

    const individualHours = (
      coverage.available_sponsored_minutes_individual / 60
    ).toFixed(1);
    const couplesHours = (
      coverage.available_sponsored_minutes_couples / 60
    ).toFixed(1);

    const { sponsored_hours: sponsoredHours } = client;
    const key = Object.keys(sponsoredHours)[0];

    return (
      <div style={sectionStyle}>
        <div style={sectionStyle}>
          <p style={titleStyle}>
            <FormattedMessage defaultMessage="Insurance Sponsored Hours" />
          </p>
          <div>
            {(isAdmin || key === 'individual') && (
              <p style={descStyle}>
                <FormattedMessage
                  defaultMessage="<b>Individual</b>: {hours} hours"
                  values={{
                    hours: getHour(individualHours),
                    b: text => <b>{text}</b>,
                  }}
                />
              </p>
            )}
            {(isAdmin || key === 'couples') && (
              <p style={descStyle}>
                <FormattedMessage
                  defaultMessage="<b>Couples</b>: {hours} hours"
                  values={{
                    hours: getHour(couplesHours),
                    b: text => <b>{text}</b>,
                  }}
                />
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Preserved from class component (was unused in render).
  // eslint-disable-next-line no-unused-vars
  const renderPsychiatricAssessment = () =>
    client.available_services?.psychiatric_assessment && (
      <div style={sectionStyle}>
        <p style={titleStyle}>
          <FormattedMessage defaultMessage="Psychiatric Assessment" />
        </p>
        <div>
          <p style={descStyle}>
            <FormattedMessage
              defaultMessage=" You can refer this client for a psychiatric assessment through Inkblot. If this is required, please email the workhealth@greenshield.ca"
              values={{
                email: (
                  // eslint-disable-next-line react/jsx-no-literals
                  <a href="mailto:workhealth@inkblottherapy.com">
                    <FormattedMessage defaultMessage="Clinical Services Team" />
                  </a>
                ),
              }}
            />
          </p>
        </div>
      </div>
    );

  const { available_services: services } = client;

  if (!services) return <DataLoadingSpinner />;

  return (
    <div className="available-services">
      {renderIntro()}
      {renderPlanSponsor()}
      {isAdmin && renderReseller()}
      {flags.pod2WhiteLabel && renderEapProductBrandName()}
      {renderRemainingSponsoredHours()}
      {renderRemainingInsuranceSponsoredHours()}
      <ServicesAvailable availableServices={services} />
      <Snackbar
        open={snackOpen}
        autoHideDuration={6000}
        onClose={() => setSnackOpen(false)}
      >
        {errorMessage ? (
          <Alert severity="error">{errorMessage}</Alert>
        ) : (
          <Alert severity="success">{message}</Alert>
        )}
      </Snackbar>
    </div>
  );
}

AvailableServicesBody.propTypes = {
  client: PropTypes.object.isRequired,
  fetchClient: PropTypes.func.isRequired,
  flags: PropTypes.object,
};

function mapStateToProps(state) {
  const { client } = state;

  return {
    client,
  };
}
export default withLDConsumer()(
  connect(mapStateToProps, { fetchClient })(AvailableServicesBody),
);
